import { isNullOrWhiteSpace, TextInputHelperCurrency } from '@honeycomb-npm/honeycomb-react';
import { Dropdown } from '@honeycomb-npm/honeycomb-react';
import {
    useEffect,
    useRef,
    useState,
} from 'react';

import { useAppSelector } from '../../../app/hooks';
import { getSoftValidationMessage } from '../../../utils/contentUtil';
import { selectGenericBladeHardValidations } from '../genericBladeSelectors';
import {
    GenericBladeDynamicContentType,
    GenericBladeParsedDataType,
    GenericSelectiveInputDropdown,
    NodePayload,
    ValidationInfo,
} from '../genericBladeTypes';
import GenericBladeFormHeading from './BladeFormHeading';

type PropsT = {
    id: string;
    genericFormAEM: Record<string, GenericBladeDynamicContentType>;
    genericFormApiData?: GenericBladeParsedDataType;
    validations?: ValidationInfo;
    handleChange: (node: string, value: NodePayload | null) => void;
    errors: Record<string, string>;
    dropdownOptions: GenericSelectiveInputDropdown
};

export const BladeSelectiveInput = ({
    id,
    genericFormAEM,
    genericFormApiData,
    validations,
    handleChange,
    errors,
    dropdownOptions,
}: PropsT): JSX.Element => {
    const hardValidations = useAppSelector((state) => selectGenericBladeHardValidations(state, id));

    const [selectedOption, setSelectedOption] = useState<Record<string, NodePayload | null>>(
        Object.entries(genericFormApiData ?? {}).reduce(
            (result, [key, data]) => ({
                ...result,
                [key]: data.value ?? '',
            }), {} as Record<string, NodePayload>,
        ),
    );

    const [selectedValue, setSelectedValue] = useState<string>('');
    const [showInput, setShowInput] = useState<boolean>(false);
    const [showError, setShowError] = useState(false);
    const [softValidationMessage, setSoftValidationMessage] = useState<string>('');

    const dropdownRef = useRef<HTMLSelectElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (Object.keys(selectedOption).length > 0) {
            const dropdownOption = dropdownOptions.options.find(option => option.value.split(':')[0] === selectedOption[dropdownOptions.key]);
            const value = dropdownOption?.value?.split(':').pop() ?? '';

            setSelectedValue(value);

            const selectedAemNode = genericFormAEM[value] as Record<string, string[]>;

            const hideInputValues = selectedAemNode ? selectedAemNode['hideInput'] : [];

            if (hideInputValues && hideInputValues.includes(selectedOption[dropdownOptions.key] as string)) {
                setShowInput(false);
            } else {
                setShowInput(true);
            }
        }
    }, [selectedOption]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            setShowError(true);

            if (errors?.[dropdownOptions.key]) {
                dropdownRef.current?.focus();
            }
            if (errors?.[selectedValue]) {
                inputRef.current?.focus();
            }
        }

        const message = getSoftValidationMessage(
            validations,
            selectedValue,
            selectedOption[selectedValue] as string,
        );

        setSoftValidationMessage(message);
    }, [errors]);

    useEffect(() => {
        if (showError) {
            if (inputRef.current !== document.activeElement) {
                inputRef.current?.focus();
            }
            inputRef.current?.blur();

            if (errors?.[dropdownOptions.key]) {
                dropdownRef.current?.focus();
            }
            if (errors?.[selectedValue]) {
                inputRef.current?.blur();
                inputRef.current?.focus();
            }
        }
    }, [showError]);

    const handleBlur = () => {
        const activeElement = document.activeElement;

        if (activeElement?.tagName === 'BUTTON') {
            return;
        }

        if (!selectedOption[dropdownOptions.key]) {
            setShowError(false);
        }
    };

    const onValueChange = (name: string, newValue: NodePayload | null) => {
        let message = '';

        if (showError) {
            setShowError(false);
        }

        if (selectedValue) {
            setSelectedOption((value) => ({
                ...value,
                [selectedValue]: newValue ? newValue : null,
            }));

            handleChange(selectedValue, newValue);

            message = getSoftValidationMessage(
                validations,
                selectedValue,
                newValue,
            );
        }

        setSoftValidationMessage(message);
    };

    const onSelectionChange = (newValue: string) => {
        setShowInput(false);

        const dropdownValue = newValue.split(':')[0];
        const inputKey = newValue.split(':').pop();

        setSelectedOption({ [dropdownOptions.key]: dropdownValue });

        setSelectedValue(inputKey ?? dropdownValue);

        const selectedAemNode = genericFormAEM[inputKey ?? dropdownValue] as Record<string, string[]>;

        const hideInputValues = selectedAemNode ? selectedAemNode['hideInput'] : [];

        if (hideInputValues && hideInputValues.includes(dropdownValue)) {
            setShowInput(false);
        } else {
            setShowInput(true);
        }

        handleChange(dropdownOptions.key, dropdownValue);

        onValueChange(inputKey ?? dropdownValue, null);
    };

    const getDropdownValue = () => {
        const dropdownOption = dropdownOptions.options?.find(option => option.value.split(':')[0] === selectedOption[dropdownOptions.key]);

        return dropdownOption?.value ?? '';
    };

    const getDropdownLabel = () => {
        const key = `${dropdownOptions.key}Label`;

        return genericFormAEM[key] ?? ' ';
    };

    const getTextboxLabel = () => {
        const selectedKeyAem = genericFormAEM[selectedValue] as Record<string, string>;

        if (!selectedKeyAem) {
            return 'label';
        }

        const key = Object.keys(selectedKeyAem).find((selectedKeyAemKey) => selectedKeyAemKey.includes('label'));

        return key ? selectedKeyAem[key] : 'label';
    };

    const getHardValidationMessage = (key: string, type: string): string | undefined => hardValidations?.[key]?.[type];

    const getHardValidationValue = (key: string, type: string): number | undefined => {
        const value = hardValidations?.[key]?.[type];

        return value ? parseInt(value) : undefined;
    };

    const getHelpText = () => {
        if(!showError && !isNullOrWhiteSpace(softValidationMessage)) {
            return softValidationMessage;
        }

        return undefined;
    };

    return (
        <>
            <GenericBladeFormHeading
                {...{
                    id,
                    node: '',
                    heading: '',
                    description: '',
                }}
            />

            <div className="row m-0 p-0">
                <div className="col-12 col-md-6 pr-md-2 p-0 mb-1">
                    <Dropdown
                        id={`generic-blade-selective-input-${id}-dropdown`}
                        name={`generic-blade-selective-input-${id}-dropdown`}
                        defaultOption="Select"
                        options={dropdownOptions.options}
                        onChange={(_: string, newValue: string) =>
                            onSelectionChange(newValue)
                        }
                        value={getDropdownValue()}
                        label={getDropdownLabel()}
                        errormessage={showError ? errors?.[dropdownOptions.key] : ''}
                        isValid={!errors?.[dropdownOptions.key] || !!selectedOption[dropdownOptions.key]}
                        hideDefaultOption={!!selectedOption[dropdownOptions.key]}
                        onBlur={() => handleBlur()}
                        ref={dropdownRef}
                    />
                </div>

                {showInput && (
                    <div className="col-12 col-md-6 pl-md-2 p-0 mb-1">
                        <TextInputHelperCurrency
                            key={selectedValue}
                            id={`generic-blade-selective-input-${id}-value`}
                            label={getTextboxLabel()}
                            onChange={(name: string, newValue: string) =>
                                onValueChange(name, newValue)
                            }
                            placeholder="$"
                            value={selectedOption[selectedValue] as string ?? ''}
                            required={!!errors?.[selectedValue] && showError}
                            min={getHardValidationValue(selectedValue, 'minValue')}
                            max={getHardValidationValue(selectedValue, 'maxValue')}
                            errormessage={showError ? errors?.[selectedValue] : ''}
                            errormessageMax={getHardValidationMessage(selectedValue, 'maxValueMessage')}
                            errormessageMin={getHardValidationMessage(selectedValue, 'minValueMessage')}
                            isValid={!errors?.[selectedValue] || !showError}
                            forwardedRef={inputRef}
                            helptext={getHelpText()}
                        />
                    </div>
                )}
            </div>
        </>
    );
};
